import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { API } from '../api/client';
import { Plus, X, Loader2, Camera, FileSignature, Trash2, AlertTriangle, Filter, Upload, Sparkles, Mail, Clock, CheckSquare, Square } from 'lucide-react';
import CsvImport from '../components/app/CsvImport';
import SearchBar, { matches } from '../components/app/SearchBar';
import { useSyncListener } from '../hooks/useDataSync';

const TYPE_LABELS = {
  suciedad: 'Suciedad / Mantenimiento',
  humedad: 'Humedades',
  impermeabilizacion: 'Goteras / Impermeabilización',
  grietas: 'Grietas',
  vegetacion: 'Vegetación excesiva',
  acceso_deteriorado: 'Acceso deteriorado',
  vallado_danado: 'Vallado dañado',
  ruidos: 'Ruidos',
  estructural: 'Estructural',
  zonas_comunes: 'Zonas comunes',
  otro: 'Otro',
};
const PRIO = {
  alta: { label: 'Alta', color: 'bg-red-500/20 text-red-300 border-red-500/40' },
  media: { label: 'Media', color: 'bg-orange-500/20 text-orange-300 border-orange-500/40' },
  baja: { label: 'Baja', color: 'bg-green-500/20 text-green-300 border-green-500/40' },
};
const STATUS = ['en_curso', 'cerrada'];
const STATUS_LABEL = { abierta: 'Abierta', en_curso: 'En curso', resuelta: 'Resuelta', cerrada: 'Cerrada' };
const CLOSING_STATES = new Set(['resuelta', 'cerrada']);
// Normaliza estados legacy → nuevos (abierta→en_curso, resuelta→cerrada)
const normStatus = (s) => (s === 'abierta' ? 'en_curso' : (s === 'resuelta' ? 'cerrada' : s));

// SLA por prioridad — días máximo antes de considerarse fuera de plazo
const SLA_DAYS = { alta: 3, media: 7, baja: 15 };

// Días desde `from` hasta `to` (o hasta hoy si `to` es null)
const daysBetween = (from, to) => {
  if (!from) return null;
  const start = new Date(from);
  const end = to ? new Date(to) : new Date();
  return Math.max(0, Math.floor((end - start) / 86400000));
};

const DaysBadge = ({ inc, size = 'sm' }) => {
  const closed = CLOSING_STATES.has(inc.status);
  const d = daysBetween(inc.created_at, closed ? inc.resolved_at : null);
  if (d == null) return null;
  const sla = SLA_DAYS[inc.priority] ?? 7;
  const ratio = d / sla;
  const color = closed
    ? (d <= sla
        ? 'text-[#A3FF12] bg-[#A3FF12]/10 border-[#A3FF12]/30'  // dentro de SLA al cerrar
        : 'text-white/60 bg-white/5 border-white/10')
    : (ratio <= 0.5 ? 'text-[#A3FF12] bg-[#A3FF12]/10 border-[#A3FF12]/30'
      : ratio < 1 ? 'text-yellow-300 bg-yellow-500/10 border-yellow-500/30'
      : 'text-red-300 bg-red-500/15 border-red-500/40 animate-pulse');
  const cls = size === 'lg'
    ? 'text-sm font-black px-3 py-1 rounded-lg border'
    : 'text-[10px] font-black px-1.5 py-0.5 rounded border';
  const overSla = !closed && d > sla;
  const label = closed
    ? `Cerrada tras ${d}d (SLA ${sla}d)`
    : (overSla ? `¡Fuera de SLA! ${d}d > ${sla}d` : `${d}d abierta · SLA ${sla}d`);
  return (
    <span className={`${cls} ${color} inline-flex items-center gap-1 font-mono`} title={label}>
      {overSla ? <AlertTriangle size={size === 'lg' ? 14 : 10}/> : <Clock size={size === 'lg' ? 14 : 10}/>}
      {d}d{size === 'lg' ? ` / ${sla}d` : ''}
    </span>
  );
};

const fileUrl = (path) => {
  const token = localStorage.getItem('sf_token');
  return `${API}/maintenance/files/${path}?auth=${token}`;
};

const Incidencias = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // kanban | list
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null); // incident id for detail modal
  const [filterPrio, setFilterPrio] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterProvince, setFilterProvince] = useState('');
  const [filterSource, setFilterSource] = useState(''); // '' | 'totem_import' | 'manual'
  const [provinces, setProvinces] = useState([]);
  const [search, setSearch] = useState('');
  const [scanning, setScanning] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [searchParams] = useSearchParams();

  // Deep-link from global search: /app/incidencias?q=xxx&focus=<id>
  useEffect(() => {
    const q = searchParams.get('q');
    const focus = searchParams.get('focus');
    if (q) setSearch(q);
    if (focus && items.length) setDetail(focus);
  }, [searchParams, items]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inc, s] = await Promise.all([api.get('/incidents'), api.get('/sites/lite')]);
      setItems(inc.data);
      setSites(s.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/incidents/provinces').then(({ data }) => setProvinces(data || [])).catch(() => {});
  }, []);
  useSyncListener(load, ['incidents', 'sites']);

  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);

  const filtered = items.filter((i) => {
    const siteProv = siteMap[i.site_id]?.province;
    const incProv = i.province || siteProv || '';
    const okProv = !filterProvince || (incProv || '').toLowerCase() === filterProvince.toLowerCase();
    const okSource = !filterSource ||
      (filterSource === 'totem_import' && i.source === 'totem_import') ||
      (filterSource === 'manual' && i.source !== 'totem_import');
    return (
      (!filterPrio || i.priority === filterPrio) &&
      (!filterType || i.type === filterType) &&
      okProv && okSource &&
      matches(search, i.title, i.description, i.notes, i.incident_number, i.site_code_manual,
        i.reporter_name, (i.reporter_phones || []).join(' '), (i.reporter_emails || []).join(' '),
        TYPE_LABELS[i.type], siteMap[i.site_id]?.name, siteMap[i.site_id]?.municipality)
    );
  });

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post('/incidents/scan-sites');
      alert(`${data.length} incidencias auto-detectadas.`);
      await load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setScanning(false); }
  };

  const del = async (id) => {
    if (!confirm('¿Eliminar esta incidencia?')) return;
    await api.delete(`/incidents/${id}`);
    await load();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((i) => i.id));
    });
  };
  const exitSelection = () => { setSelectionMode(false); setSelectedIds(new Set()); };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`¿Eliminar ${selectedIds.size} incidencia(s)? Esta acción no se puede deshacer.`)) return;
    setDeletingBulk(true);
    try {
      await api.post('/incidents/bulk-delete', { ids: Array.from(selectedIds) });
      exitSelection();
      await load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setDeletingBulk(false); }
  };
  const bulkDeleteAllFiltered = async () => {
    const q = { status: filterPrio ? undefined : undefined, priority: filterPrio || undefined, type: filterType || undefined };
    const scope = [];
    if (filterPrio) scope.push(`prioridad "${filterPrio}"`);
    if (filterType) scope.push(`tipo "${TYPE_LABELS[filterType] || filterType}"`);
    const scopeTxt = scope.length ? ` con ${scope.join(' y ')}` : ' (TODAS las incidencias visibles según filtros)';
    if (!confirm(`⚠️ Vas a eliminar TODAS las incidencias${scopeTxt}. Esta acción es masiva e irreversible. ¿Continuar?`)) return;
    if (!confirm('Última confirmación: ¿estás seguro?')) return;
    setDeletingBulk(true);
    try {
      const { data } = await api.post('/incidents/bulk-delete', { all_filtered: true, ...q });
      exitSelection();
      await load();
      alert(`${data.deleted} incidencia(s) eliminadas.`);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setDeletingBulk(false); }
  };

  const moveStatus = async (inc, newStatus) => {
    // Al cerrar sin evidencia, avisamos y damos opción de abrir el detalle para completarla
    if (CLOSING_STATES.has(newStatus)) {
      const hasDespuesPhotos = (inc.photos || []).some((p) => p.kind === 'despues');
      const hasActa = (inc.actas || []).length > 0;
      if (!hasDespuesPhotos || !hasActa) {
        const missing = [];
        if (!hasDespuesPhotos) missing.push('fotos "después"');
        if (!hasActa) missing.push('acta de aceptación');
        const proceed = window.confirm(
          `Vas a cerrar esta incidencia sin ${missing.join(' ni ')}.\n\n`
          + 'Aceptar: abro el detalle para adjuntar la evidencia antes de cerrar.\n'
          + 'Cancelar: continuar y cerrar igualmente.'
        );
        if (proceed) { setDetail(inc.id); return; }
      }
    }
    const { id, ...rest } = inc;
    await api.put(`/incidents/${id}`, { ...rest, status: newStatus, photos: undefined, actas: undefined, created_by: undefined, created_at: undefined, updated_at: undefined });
    await load();
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="incidents-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12]">MANTENIMIENTO · CORRECTIVO</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1">Gestión de incidencias</h1>
          <p className="text-white/50 text-sm mt-1">Anomalías detectadas (urbano + rural) con prioridad y trazabilidad completa.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <CsvImport entity="incidents" onImported={load}/>
          <button onClick={() => setShowEmailModal(true)} data-testid="btn-open-email-parser"
            className="flex items-center gap-2 border border-purple-400/50 text-purple-300 hover:bg-purple-500/10 px-3 py-2 rounded-xl text-sm font-bold">
            <Mail size={14}/> Desde email
          </button>
          <button onClick={scan} disabled={scanning} data-testid="btn-auto-detect"
            className="flex items-center gap-2 border border-[#A3FF12]/40 text-[#A3FF12] hover:bg-[#A3FF12]/10 px-3 py-2 rounded-xl text-sm font-bold">
            {scanning ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>}
            Auto-detectar
          </button>
          <button onClick={() => setSelectionMode((v) => !v)} data-testid="btn-toggle-selection"
            className={`flex items-center gap-2 border px-3 py-2 rounded-xl text-sm font-bold ${selectionMode ? 'bg-red-500/15 border-red-400/60 text-red-200' : 'border-white/15 text-white/70 hover:bg-white/5'}`}>
            {selectionMode ? <CheckSquare size={14}/> : <Square size={14}/>}
            {selectionMode ? 'Salir selección' : 'Seleccionar'}
          </button>
          <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="btn-new-incident"
            className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl hover:bg-[#8ee00a] text-sm">
            <Plus size={16}/> Nueva
          </button>
        </div>
      </div>

      {/* Barra de selección múltiple */}
      {selectionMode && (
        <div data-testid="bulk-actions-bar"
          className="sticky top-2 z-30 mb-3 bg-red-500/15 border border-red-400/50 rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap backdrop-blur">
          <button onClick={toggleSelectAll} data-testid="btn-select-all-visible"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
            {selectedIds.size === filtered.length && filtered.length > 0
              ? <><CheckSquare size={13}/> Deseleccionar todas</>
              : <><Square size={13}/> Seleccionar todas ({filtered.length})</>}
          </button>
          <span className="text-red-100 text-xs font-black">
            {selectedIds.size} seleccionada{selectedIds.size === 1 ? '' : 's'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button onClick={bulkDelete} disabled={deletingBulk} data-testid="btn-bulk-delete"
                className="flex items-center gap-1 bg-red-500 hover:bg-red-400 text-white text-xs font-black px-3 py-1.5 rounded-lg disabled:opacity-40">
                {deletingBulk ? <Loader2 className="animate-spin" size={12}/> : <Trash2 size={12}/>}
                Eliminar {selectedIds.size}
              </button>
            )}
            <button onClick={bulkDeleteAllFiltered} disabled={deletingBulk || filtered.length === 0}
              data-testid="btn-bulk-delete-all"
              className="flex items-center gap-1 bg-red-700 hover:bg-red-600 text-white text-[11px] font-black px-3 py-1.5 rounded-lg disabled:opacity-40"
              title="Borra todas las incidencias que cumplan los filtros activos">
              <Trash2 size={12}/> Borrar TODAS ({filtered.length})
            </button>
            <button onClick={exitSelection} className="text-white/70 hover:text-white text-xs font-bold px-2">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={14} className="text-white/40"/>
        <select value={filterPrio} onChange={(e) => setFilterPrio(e.target.value)} data-testid="filter-priority" className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white">
          <option value="">Todas las prioridades</option>
          <option value="alta">🔴 Alta</option>
          <option value="media">🟠 Media</option>
          <option value="baja">🟢 Baja</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="filter-type" className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white">
          <option value="">Todos los tipos</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterProvince} onChange={(e) => setFilterProvince(e.target.value)} data-testid="filter-province" className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white">
          <option value="">Todas las provincias</option>
          {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="flex bg-black/40 border border-white/10 rounded-lg overflow-hidden" data-testid="filter-source">
          <button onClick={() => setFilterSource('')} className={`px-2.5 py-1.5 text-[11px] font-bold ${filterSource===''?'bg-white/10 text-white':'text-white/50'}`}>Todas</button>
          <button onClick={() => setFilterSource('totem_import')} className={`px-2.5 py-1.5 text-[11px] font-bold ${filterSource==='totem_import'?'bg-blue-500/30 text-blue-100':'text-white/50'}`}>Histórico TOTEM</button>
          <button onClick={() => setFilterSource('manual')} className={`px-2.5 py-1.5 text-[11px] font-bold ${filterSource==='manual'?'bg-[#A3FF12]/20 text-[#A3FF12]':'text-white/50'}`}>Creadas equipo</button>
        </div>
        <div className="ml-auto flex bg-black/40 border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => setView('kanban')} className={`px-3 py-1.5 text-xs font-bold ${view==='kanban'?'bg-[#A3FF12] text-black':'text-white/60'}`}>Kanban</button>
          <button onClick={() => setView('list')} className={`px-3 py-1.5 text-xs font-bold ${view==='list'?'bg-[#A3FF12] text-black':'text-white/60'}`}>Lista</button>
        </div>
      </div>
      <div className="mb-4"><SearchBar value={search} onChange={setSearch} placeholder="Buscar por título, site o descripción…" testId="incidents-search"/></div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
          <AlertTriangle className="mx-auto text-white/20 mb-2" size={40}/>
          <p className="text-white/50">No hay incidencias registradas todavía.</p>
        </div>
      ) : view === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {STATUS.map((st) => {
            const isCerrada = st === 'cerrada';
            const bucket = filtered.filter((i) => normStatus(i.status) === st);
            return (
            <div key={st} className={`border rounded-2xl p-3 min-h-[300px] ${isCerrada ? 'bg-white/[0.02] border-white/5' : 'bg-black/30 border-white/10'}`}>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/10">
                <span className="text-xs uppercase tracking-wider text-white/60 font-bold">{STATUS_LABEL[st]}</span>
                <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full">{bucket.length}</span>
              </div>
              {isCerrada ? (
                <div className="space-y-1.5">
                  {bucket.slice(0, 200).map((inc) => (
                    <button key={inc.id}
                      onClick={() => { if (selectionMode) toggleSelect(inc.id); else setDetail(inc.id); }}
                      data-testid={`incident-card-${inc.id}`}
                      className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${selectionMode && selectedIds.has(inc.id) ? 'bg-red-500/10 ring-1 ring-red-400/40' : 'hover:bg-white/5'}`}>
                      {selectionMode && (
                        <input type="checkbox" checked={selectedIds.has(inc.id)} readOnly
                          data-testid={`chk-inc-${inc.id}`}
                          className="w-3 h-3 accent-red-400"/>
                      )}
                      {inc.incident_number && (
                        <span className="text-[10px] font-mono font-bold text-blue-300/80 flex-shrink-0">#{inc.incident_number}</span>
                      )}
                      <span className="text-white/70 truncate flex-1">{TYPE_LABELS[inc.type]}</span>
                      <span className="text-white/40 text-[10px] font-mono flex-shrink-0">{siteMap[inc.site_id]?.name || inc.site_code_manual || '—'}</span>
                    </button>
                  ))}
                  {bucket.length > 200 && (
                    <div className="text-center text-white/40 text-[11px] pt-2">…y {bucket.length - 200} más. Usa la vista Lista para ver todas.</div>
                  )}
                </div>
              ) : (
              <div className="space-y-2">
                {bucket.map((inc) => (
                  <div key={inc.id}
                    onClick={() => { if (selectionMode) toggleSelect(inc.id); else setDetail(inc.id); }}
                    data-testid={`incident-card-${inc.id}`}
                    className={`bg-[#0a0a0a] border rounded-xl p-3 cursor-pointer transition-all ${selectionMode && selectedIds.has(inc.id) ? 'border-red-400/70 ring-2 ring-red-400/40' : 'border-white/10 hover:border-[#A3FF12]/50'}`}>
                    {selectionMode && (
                      <div className="flex items-center gap-1 mb-1.5 text-[10px] text-white/60">
                        <input type="checkbox" checked={selectedIds.has(inc.id)} readOnly
                          data-testid={`chk-inc-${inc.id}`}
                          className="w-3.5 h-3.5 accent-red-400"/>
                        <span>Seleccionar</span>
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${PRIO[inc.priority].color}`}>{PRIO[inc.priority].label}</span>
                      <div className="flex items-center gap-1 flex-wrap justify-end">
                        <DaysBadge inc={inc}/>
                        {inc.incident_number && (
                          <span className="text-[9px] font-mono font-bold text-blue-300 bg-blue-500/10 border border-blue-500/30 px-1.5 py-0.5 rounded" title="Nº incidencia">
                            #{inc.incident_number}
                          </span>
                        )}
                        {inc.auto_generated && <span className="text-[10px] text-[#A3FF12]/70">AUTO</span>}
                      </div>
                    </div>
                    <div className="text-white font-bold text-sm truncate">{inc.title}</div>
                    <div className="text-white/40 text-xs mt-1">{TYPE_LABELS[inc.type]}</div>
                    <div className="text-white/50 text-xs mt-1 truncate">
                      {siteMap[inc.site_id]?.name || inc.site_code_manual || inc.site_id || 'Sin site'}
                      {inc.site_code_manual && siteMap[inc.site_id] && (
                        <span className="ml-1 text-blue-300/70 text-[9px] font-mono">· {inc.site_code_manual}</span>
                      )}
                      {inc.site_code_manual && !siteMap[inc.site_id] && <span className="ml-1 text-orange-300/70 text-[9px]">(manual)</span>}
                    </div>
                    {siteMap[inc.site_id]?.address && (
                      <div className="text-white/40 text-[10px] mt-0.5 truncate" title={siteMap[inc.site_id]?.address}>
                        📍 {siteMap[inc.site_id]?.address}
                      </div>
                    )}
                    {(inc.reporter_name || inc.reporter_phones?.length || inc.reporter_emails?.length) && (
                      <div className="text-blue-200/70 text-[10px] mt-1 flex flex-wrap gap-1.5">
                        {inc.reporter_name && <span title="Reporta">👤 {inc.reporter_name}</span>}
                        {inc.reporter_phones?.[0] && <span title="Teléfono">📞 {inc.reporter_phones[0]}</span>}
                        {inc.reporter_emails?.[0] && <span className="truncate max-w-[140px]" title={inc.reporter_emails[0]}>✉️ {inc.reporter_emails[0]}</span>}
                      </div>
                    )}
                    <div className="flex gap-2 items-center mt-2 text-[10px] text-white/40">
                      {inc.photos?.length > 0 && <span className="flex items-center gap-1"><Camera size={10}/> {inc.photos.length}</span>}
                      {inc.actas?.length > 0 && <span className="flex items-center gap-1 text-[#A3FF12]"><FileSignature size={10}/> firmada</span>}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-black/30 border border-white/10 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-xs uppercase">
              <tr>
                {selectionMode && (
                  <th className="text-left px-2 py-3 w-8">
                    <input type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll}
                      data-testid="chk-select-all-header"
                      className="w-4 h-4 accent-red-400"/>
                  </th>
                )}
                <th className="text-left px-4 py-3">Nº</th>
                <th className="text-left px-4 py-3">Días</th>
                <th className="text-left px-4 py-3">Título</th>
                <th className="text-left px-4 py-3">Site</th>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-left px-4 py-3">Prio</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3">Coste</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inc) => (
                <tr key={inc.id}
                  className={`border-t border-white/5 hover:bg-white/5 ${selectionMode && selectedIds.has(inc.id) ? 'bg-red-500/10' : ''}`}>
                  {selectionMode && (
                    <td className="px-2 py-3">
                      <input type="checkbox" checked={selectedIds.has(inc.id)}
                        onChange={() => toggleSelect(inc.id)}
                        data-testid={`chk-row-${inc.id}`}
                        className="w-4 h-4 accent-red-400"/>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    {inc.incident_number
                      ? <span className="text-[10px] font-mono font-bold text-blue-300 bg-blue-500/10 border border-blue-500/30 px-2 py-0.5 rounded">{inc.incident_number}</span>
                      : <span className="text-white/30 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3"><DaysBadge inc={inc}/></td>
                  <td className="px-4 py-3 font-bold truncate max-w-xs">{inc.title}</td>
                  <td className="px-4 py-3 text-white/70">
                    <div>{siteMap[inc.site_id]?.name || inc.site_code_manual || inc.site_id || '—'}
                      {inc.site_code_manual && siteMap[inc.site_id] && (
                        <span className="ml-1 text-blue-300/70 text-[10px] font-mono">· {inc.site_code_manual}</span>
                      )}
                      {inc.site_code_manual && !siteMap[inc.site_id] && <span className="ml-1 text-orange-300/70 text-[9px]">(manual)</span>}
                    </div>
                    {siteMap[inc.site_id]?.address && <div className="text-white/40 text-[10px] truncate max-w-xs" title={siteMap[inc.site_id]?.address}>📍 {siteMap[inc.site_id]?.address}</div>}
                    {(inc.reporter_name || inc.reporter_phones?.length) && (
                      <div className="text-blue-200/70 text-[10px] flex gap-2 flex-wrap">
                        {inc.reporter_name && <span>👤 {inc.reporter_name}</span>}
                        {inc.reporter_phones?.[0] && <span>📞 {inc.reporter_phones[0]}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/60 text-xs">{TYPE_LABELS[inc.type]}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${PRIO[inc.priority].color}`}>{PRIO[inc.priority].label}</span></td>
                  <td className="px-4 py-3">
                    <select value={normStatus(inc.status)} onChange={(e) => moveStatus(inc, e.target.value)} className="bg-transparent border border-white/10 rounded px-2 py-1 text-xs">
                      {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-white/70">{inc.actual_cost || inc.estimated_cost || 0} €</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setDetail(inc.id)} className="text-[#A3FF12] hover:underline text-xs mr-3">Abrir</button>
                    <button onClick={() => del(inc.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <IncidentModal sites={sites} editing={editing} onClose={() => setShowModal(false)} onSaved={async () => { setShowModal(false); await load(); }}/>}
      {detail && <IncidentDetail id={detail} onClose={() => setDetail(null)} onChanged={load} siteMap={siteMap}/>}
      {showEmailModal && <EmailParserModal sites={sites} onClose={() => setShowEmailModal(false)}
        onCreated={async (incId) => { setShowEmailModal(false); await load(); if (incId) setDetail(incId); }}
        onDraft={(draft) => { setShowEmailModal(false); setEditing(draft); setShowModal(true); }}/>}
    </div>
  );
};

// ---------- Modal: create/edit ----------
const IncidentModal = ({ sites, editing, onClose, onSaved }) => {
  const [form, setForm] = useState(editing || {
    site_id: '',
    site_code_manual: '',
    incident_number: '',
    type: 'suciedad',
    title: '',
    description: '',
    priority: 'media',
    status: 'en_curso',
    estimated_cost: 0,
    actual_cost: 0,
    notes: '',
  });
  const [siteSearch, setSiteSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredSites = useMemo(() => {
    if (!siteSearch) return sites;
    const q = siteSearch.toLowerCase();
    return sites.filter((s) =>
      (s.name || '').toLowerCase().includes(q)
      || (s.code || '').toLowerCase().includes(q)
      || (s.address || '').toLowerCase().includes(q)
      || (s.municipality || '').toLowerCase().includes(q));
  }, [sites, siteSearch]);

  const selectedSite = sites.find((s) => s.id === form.site_id);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.site_id && !form.site_code_manual) {
      alert('Selecciona un emplazamiento o introduce un código manual');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Cuando es código manual, mandamos site_id como el código libre también
      if (payload.site_code_manual && !payload.site_id) payload.site_id = payload.site_code_manual;
      if (editing?.id) await api.put(`/incidents/${editing.id}`, payload);
      else await api.post('/incidents', payload);
      onSaved();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="incident-modal"
        className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-white/10 flex justify-between">
          <h3 className="text-lg font-black">{editing ? 'Editar' : 'Nueva'} incidencia</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-3">
          {/* Emplazamiento: combobox + código manual */}
          <div>
            <span className="text-xs uppercase text-white/60">Emplazamiento *</span>
            {form.site_id || form.site_code_manual ? (
              <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 mt-1 ${form.site_code_manual && !selectedSite ? 'bg-orange-500/10 border border-orange-500/40' : 'bg-[#A3FF12]/10 border border-[#A3FF12]/40'}`}>
                <div className="min-w-0">
                  <div className="text-white font-bold text-sm truncate">
                    {selectedSite ? selectedSite.name : form.site_code_manual}
                    {selectedSite?.code && <span className="text-white/50 font-normal text-xs"> · {selectedSite.code}</span>}
                    {form.site_code_manual && !selectedSite && <span className="text-orange-300 font-normal text-[10px] ml-2">CÓDIGO MANUAL</span>}
                  </div>
                  <div className="text-white/50 text-xs truncate">
                    {selectedSite?.address || selectedSite?.municipality || (form.site_code_manual && !selectedSite ? 'Sin site en portfolio — solo se registra en la incidencia' : '')}
                  </div>
                </div>
                <button type="button" onClick={() => { setForm({ ...form, site_id: '', site_code_manual: '' }); setSiteSearch(''); }}
                  data-testid="btn-change-incident-site"
                  className="text-xs text-[#A3FF12] hover:underline">Cambiar</button>
              </div>
            ) : (
              <>
                <input value={siteSearch} onChange={(e) => setSiteSearch(e.target.value)}
                  placeholder="Buscar por nombre, código, dirección o municipio…"
                  data-testid="incident-site-search"
                  className="w-full bg-black/40 border border-white/10 focus:border-[#A3FF12]/50 rounded-lg px-3 py-2 mt-1 outline-none"/>
                {siteSearch && (
                  <>
                    <div className="mt-1 max-h-48 overflow-y-auto border border-white/10 rounded-lg bg-black/30 divide-y divide-white/5">
                      {filteredSites.length === 0 ? (
                        <div className="p-3 text-white/40 text-xs text-center">Ningún site coincide con la búsqueda.</div>
                      ) : filteredSites.slice(0, 30).map((s) => (
                        <button type="button" key={s.id} onClick={() => { setForm({ ...form, site_id: s.id, site_code_manual: '' }); setSiteSearch(''); }}
                          data-testid={`incident-select-site-${s.id}`}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-[#A3FF12]/10 hover:text-[#A3FF12] text-white/80">
                          <div className="font-bold flex items-center gap-2">
                            <span className="truncate">{s.name}</span>
                            {s.code && <span className="text-white/40 font-normal">· {s.code}</span>}
                          </div>
                          <div className="text-white/40 text-[10px] truncate mt-0.5">{s.address || s.municipality || '—'}</div>
                        </button>
                      ))}
                      {filteredSites.length > 30 && (
                        <div className="px-3 py-1.5 text-[10px] text-white/40 text-center">Mostrando 30 de {filteredSites.length}</div>
                      )}
                    </div>
                    {!filteredSites.some((s) => (s.code || '').toLowerCase() === siteSearch.trim().toLowerCase() || (s.name || '').toLowerCase() === siteSearch.trim().toLowerCase()) && siteSearch.trim().length >= 2 && (
                      <button type="button" onClick={() => { setForm({ ...form, site_code_manual: siteSearch.trim(), site_id: '' }); setSiteSearch(''); }}
                        data-testid="incident-btn-use-manual-code"
                        className="w-full mt-1 border border-dashed border-orange-500/40 hover:bg-orange-500/10 text-orange-300 rounded-lg px-3 py-2 text-xs flex items-center gap-2">
                        <Plus size={12}/> Usar <b className="text-orange-200">"{siteSearch.trim()}"</b> como código manual
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          <label className="block">
            <span className="text-xs uppercase text-white/60">Nº incidencia (interna / propietario)</span>
            <input value={form.incident_number || ''} onChange={(e) => setForm({ ...form, incident_number: e.target.value })}
              placeholder="Ej. INC-2026-0042, EXP-11/2026, Ref. propietario…"
              data-testid="input-incident-number"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Tipo</span>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Prioridad</span>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="input-priority"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                <option value="alta">🔴 Alta</option>
                <option value="media">🟠 Media</option>
                <option value="baja">🟢 Baja</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase text-white/60">Título</span>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="input-title"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
          </label>
          <label className="block">
            <span className="text-xs uppercase text-white/60">Descripción</span>
            <textarea rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Coste estimado (€)</span>
              <input type="number" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: parseFloat(e.target.value) || 0 })}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Coste real (€)</span>
              <input type="number" value={form.actual_cost} onChange={(e) => setForm({ ...form, actual_cost: parseFloat(e.target.value) || 0 })}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
            </label>
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 rounded-xl text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="btn-save-incident"
            className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------- Detail: photos, acta, cost, timeline ----------
const IncidentDetail = ({ id, onClose, onChanged, siteMap }) => {
  const [inc, setInc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('antes');

  const load = async () => {
    const { data } = await api.get(`/incidents/${id}`);
    setInc(data);
  };
  useEffect(() => { load(); }, [id]);

  const uploadPhoto = async (file, kind) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    try {
      const { data } = await api.post(`/incidents/${id}/photos`, fd);
      setInc(data);
      onChanged?.();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); }
  };

  const uploadActa = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/incidents/${id}/actas`, fd);
      setInc(data);
      onChanged?.();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); }
  };

  const delPhoto = async (pid) => {
    if (!confirm('¿Eliminar foto?')) return;
    const { data } = await api.delete(`/incidents/${id}/photos/${pid}`);
    setInc(data);
  };
  const delActa = async (aid) => {
    if (!confirm('¿Eliminar acta?')) return;
    const { data } = await api.delete(`/incidents/${id}/actas/${aid}`);
    setInc(data);
  };

  if (!inc) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#A3FF12]" size={32}/>
      </div>
    );
  }

  const photosAntes = inc.photos?.filter((p) => p.kind === 'antes') || [];
  const photosDespues = inc.photos?.filter((p) => p.kind === 'despues') || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="incident-detail"
        className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-white/10 flex justify-between items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${PRIO[inc.priority].color}`}>{PRIO[inc.priority].label}</span>
              <span className="text-[10px] uppercase text-white/40">{TYPE_LABELS[inc.type]}</span>
              {inc.incident_number && (
                <span className="text-[10px] font-mono font-bold text-blue-300 bg-blue-500/10 border border-blue-500/30 px-2 py-0.5 rounded">#{inc.incident_number}</span>
              )}
              {inc.auto_generated && <span className="text-[10px] text-[#A3FF12]/70">AUTO</span>}
            </div>
            <h3 className="text-xl font-black">{inc.title}</h3>
            <p className="text-white/50 text-sm mt-1">
              {siteMap[inc.site_id]?.name || inc.site_code_manual || inc.site_id || 'Sin site'} · {STATUS_LABEL[inc.status]}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DaysBadge inc={inc} size="lg"/>
            <button onClick={onClose} className="text-white/40 hover:text-white p-1"><X size={22}/></button>
          </div>
        </div>

        <div className="flex border-b border-white/10 px-4 overflow-x-auto">
          {[
            { id: 'antes', label: `Antes (${photosAntes.length})`, icon: Camera },
            { id: 'despues', label: `Después (${photosDespues.length})`, icon: Camera },
            { id: 'acta', label: `Acta firmada (${inc.actas?.length || 0})`, icon: FileSignature },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 whitespace-nowrap ${
                tab === t.id ? 'text-[#A3FF12] border-[#A3FF12]' : 'text-white/50 border-transparent'
              }`}><t.icon size={14}/> {t.label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {inc.description && tab === 'antes' && (
            <div className="bg-black/30 border border-white/5 rounded-xl p-3 text-sm text-white/70 mb-3">{inc.description}</div>
          )}

          {(tab === 'antes' || tab === 'despues') && (
            <>
              <label className="border border-dashed border-white/20 hover:border-[#A3FF12]/50 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer text-white/50 hover:text-[#A3FF12]">
                {uploading ? <Loader2 className="animate-spin" size={24}/> : <Upload size={24}/>}
                <span className="text-xs mt-2">Subir foto {tab}</span>
                <input type="file" accept="image/*" className="hidden" data-testid={`upload-photo-${tab}`}
                  onChange={(e) => uploadPhoto(e.target.files[0], tab)}/>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(tab === 'antes' ? photosAntes : photosDespues).map((p) => (
                  <div key={p.id} className="relative group rounded-xl overflow-hidden border border-white/10 aspect-square">
                    <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover"/>
                    <button onClick={() => delPhoto(p.id)} className="absolute top-1 right-1 bg-black/70 text-red-400 rounded p-1 opacity-0 group-hover:opacity-100">
                      <Trash2 size={12}/>
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'acta' && (
            <>
              <label className="border border-dashed border-white/20 hover:border-[#A3FF12]/50 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer text-white/50 hover:text-[#A3FF12]">
                {uploading ? <Loader2 className="animate-spin" size={24}/> : <FileSignature size={24}/>}
                <span className="text-xs mt-2">Subir acta de aceptación firmada (PDF/imagen)</span>
                <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="upload-acta"
                  onChange={(e) => uploadActa(e.target.files[0])}/>
              </label>
              <div className="space-y-2 mt-2">
                {inc.actas?.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 bg-black/30 border border-white/10 rounded-xl p-3">
                    <FileSignature className="text-[#A3FF12]" size={20}/>
                    <a href={fileUrl(a.storage_path)} target="_blank" rel="noreferrer" className="flex-1 text-white hover:text-[#A3FF12] truncate">{a.original_filename}</a>
                    <button onClick={() => delActa(a.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                  </div>
                ))}
                {(!inc.actas || inc.actas.length === 0) && <p className="text-white/40 text-sm text-center py-4">Sin acta firmada todavía.</p>}
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex justify-between items-center gap-2 flex-wrap">
          <select value={normStatus(inc.status)} onChange={async (e) => {
              const st = e.target.value;
              // Bloqueo suave si se cierra sin evidencia
              if (CLOSING_STATES.has(st) && st !== inc.status) {
                const hasDespues = (inc.photos || []).some((p) => p.kind === 'despues');
                const hasActa = (inc.actas || []).length > 0;
                if (!hasDespues || !hasActa) {
                  const missing = [];
                  if (!hasDespues) missing.push('fotos "después"');
                  if (!hasActa) missing.push('acta de aceptación firmada');
                  const cont = window.confirm(
                    `Falta adjuntar ${missing.join(' y ')} antes de cerrar.\n\n`
                    + 'Cancelar: quedarme aquí para adjuntarla.\n'
                    + 'Aceptar: cerrar igualmente (queda registrado sin evidencia).'
                  );
                  if (!cont) { e.target.value = inc.status; return; }
                }
              }
              await api.put(`/incidents/${id}`, {
                site_id: inc.site_id, type: inc.type, title: inc.title, description: inc.description,
                priority: inc.priority, status: st, estimated_cost: inc.estimated_cost, actual_cost: inc.actual_cost,
                assigned_to: inc.assigned_to, notes: inc.notes,
                incident_number: inc.incident_number, site_code_manual: inc.site_code_manual,
              });
              await load(); onChanged?.();
            }} data-testid="detail-status" className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs">
            {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <div className="flex items-center gap-3 text-xs text-white/40">
            <span title="Fotos 'después' cargadas">
              <Camera size={11} className={`inline mr-1 ${photosDespues.length ? 'text-[#A3FF12]' : 'text-white/30'}`}/>
              {photosDespues.length}
            </span>
            <span title="Actas firmadas">
              <FileSignature size={11} className={`inline mr-1 ${(inc.actas||[]).length ? 'text-[#A3FF12]' : 'text-white/30'}`}/>
              {(inc.actas || []).length}
            </span>
            <span>Coste: <span className="text-white font-bold">{inc.actual_cost || inc.estimated_cost || 0} €</span></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Modal: pegar email → parsear → prellenar / autocrear ============
const CONFIDENCE_META = {
  alta:  { label: 'ALTA',  color: '#22c55e' },
  media: { label: 'MEDIA', color: '#fbbf24' },
  baja:  { label: 'BAJA',  color: '#ef4444' },
};

const EmailParserModal = ({ sites, onClose, onCreated, onDraft }) => {
  const [emailText, setEmailText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [overrideSiteId, setOverrideSiteId] = useState('');

  const analyze = async (autoCreate = false) => {
    if (emailText.trim().length < 20) {
      alert('Pega el texto del email (mínimo 20 caracteres).');
      return;
    }
    if (autoCreate) setCreating(true); else setAnalyzing(true);
    try {
      const { data } = await api.post('/incidents/parse-email', {
        email_text: emailText, auto_create: autoCreate,
      });
      setParsed(data);
      setOverrideSiteId(data.matched_site_id || '');
      if (autoCreate && data.created_incident_id) {
        onCreated(data.created_incident_id);
      }
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAnalyzing(false); setCreating(false);
    }
  };

  const useAsDraft = () => {
    if (!parsed) return;
    const finalSiteId = overrideSiteId || parsed.matched_site_id || '';
    if (!finalSiteId) {
      alert('Selecciona manualmente un emplazamiento antes de continuar.');
      return;
    }
    const extraNotes = [
      parsed.incident_ref_extern && `Ref externa: ${parsed.incident_ref_extern}`,
      parsed.contact_name && `Contacto: ${parsed.contact_name}`,
      parsed.contact_email && `Email: ${parsed.contact_email}`,
      parsed.contact_phone && `Teléfono: ${parsed.contact_phone}`,
      parsed.address && `Dirección reportada: ${parsed.address}`,
      parsed.notes,
    ].filter(Boolean).join('\n');
    onDraft({
      site_id: finalSiteId,
      type: parsed.type || 'otro',
      title: parsed.title || 'Incidencia reportada por email',
      description: parsed.description || '',
      priority: parsed.priority || 'media',
      status: 'en_curso',
      estimated_cost: 0,
      actual_cost: 0,
      notes: extraNotes,
    });
  };

  const createNow = () => analyze(true);

  const conf = CONFIDENCE_META[parsed?.confidence] || CONFIDENCE_META.media;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="email-parser-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-purple-500/30 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-white/10 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-black tracking-widest text-purple-400">ADQUIRIAM · PARSEO DE EMAIL</div>
            <h3 className="text-lg font-black text-white flex items-center gap-2 mt-1">
              <Mail size={18} className="text-purple-400"/> Abrir incidencia desde email
            </h3>
            <p className="text-white/50 text-xs mt-1">Pega el correo íntegro y Adquiriam extraerá site, contacto, tipo, prioridad y descripción.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs uppercase text-white/60">Texto del email</span>
            <textarea rows="9" value={emailText} onChange={(e) => setEmailText(e.target.value)}
              placeholder="Pega aquí el email completo (asunto, cuerpo, firma…)"
              data-testid="email-parser-textarea"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 text-sm text-white/90 font-mono"/>
            <span className="text-[10px] text-white/40">{emailText.length} caracteres</span>
          </label>

          {!parsed && (
            <div className="flex gap-2">
              <button onClick={() => analyze(false)} disabled={analyzing || emailText.length < 20}
                data-testid="btn-analyze-email"
                className="flex-1 bg-purple-500 text-white font-black text-sm py-2.5 rounded-xl hover:bg-purple-400 disabled:opacity-40 flex items-center justify-center gap-2">
                {analyzing ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>}
                {analyzing ? 'Analizando…' : 'Analizar con Adquiriam'}
              </button>
              <button onClick={createNow} disabled={creating || emailText.length < 20}
                data-testid="btn-analyze-and-create"
                className="flex-1 bg-[#A3FF12] text-black font-black text-sm py-2.5 rounded-xl hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center justify-center gap-2">
                {creating ? <Loader2 className="animate-spin" size={14}/> : <Plus size={14}/>}
                {creating ? 'Creando…' : 'Analizar y crear directamente'}
              </button>
            </div>
          )}

          {parsed && (
            <div className="space-y-3 border-t border-white/10 pt-4" data-testid="parsed-result">
              <div className="flex items-center gap-2">
                <span className="text-xs font-black px-2 py-0.5 rounded"
                  style={{ backgroundColor: `${conf.color}20`, color: conf.color, border: `1px solid ${conf.color}50` }}>
                  Confianza Adquiriam: {conf.label}
                </span>
                {parsed.matched_site_id ? (
                  <span className="text-[10px] font-bold text-[#A3FF12] bg-[#A3FF12]/10 px-2 py-0.5 rounded border border-[#A3FF12]/30">
                    ✓ Site matcheado: {parsed.matched_site_name}
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-orange-300 bg-orange-500/10 px-2 py-0.5 rounded border border-orange-500/30">
                    ⚠ Sin match automático — selecciona manualmente
                  </span>
                )}
              </div>

              {!parsed.matched_site_id && (
                <label className="block">
                  <span className="text-xs uppercase text-white/60">Selecciona emplazamiento manualmente</span>
                  {parsed.site_hint && <div className="text-[11px] text-orange-300 mt-0.5">Pista Adquiriam: <i>{parsed.site_hint}</i></div>}
                  <select value={overrideSiteId} onChange={(e) => setOverrideSiteId(e.target.value)}
                    data-testid="manual-site-select"
                    className="w-full bg-black/40 border border-orange-500/30 rounded-lg px-3 py-2 mt-1 text-white">
                    <option value="">— Elige site —</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.municipality || ''}</option>)}
                  </select>
                </label>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs">
                <ParsedField label="Título" value={parsed.title}/>
                <ParsedField label="Tipo" value={TYPE_LABELS[parsed.type] || parsed.type}/>
                <ParsedField label="Prioridad" value={parsed.priority?.toUpperCase()} highlight={parsed.priority === 'alta' ? '#ef4444' : null}/>
                <ParsedField label="Ref externa" value={parsed.incident_ref_extern || '—'}/>
                <ParsedField label="Contacto" value={parsed.contact_name || '—'}/>
                <ParsedField label="Teléfono" value={parsed.contact_phone || '—'}/>
                <ParsedField label="Email contacto" value={parsed.contact_email || '—'} full/>
                <ParsedField label="Dirección" value={parsed.address || '—'} full/>
              </div>

              <div className="bg-black/40 border border-white/10 rounded-lg p-3">
                <div className="text-[10px] uppercase text-white/50 font-bold">Descripción propuesta</div>
                <p className="text-white/80 text-xs mt-1 whitespace-pre-wrap">{parsed.description}</p>
              </div>

              <div className="flex gap-2 pt-2 border-t border-white/10">
                <button onClick={() => { setParsed(null); }} className="px-4 py-2 border border-white/10 rounded-xl text-xs text-white/70 hover:bg-white/5">
                  ← Reanalizar
                </button>
                <button onClick={useAsDraft} data-testid="btn-use-as-draft"
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white font-black text-xs py-2 rounded-xl flex items-center justify-center gap-2">
                  <FileSignature size={13}/> Abrir formulario con estos datos
                </button>
                <button onClick={createNow} disabled={creating || !(overrideSiteId || parsed.matched_site_id)}
                  data-testid="btn-create-directly"
                  className="flex-1 bg-[#A3FF12] hover:bg-[#b8ff3a] text-black font-black text-xs py-2 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40">
                  {creating ? <Loader2 className="animate-spin" size={13}/> : <Plus size={13}/>}
                  Crear incidencia YA
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ParsedField = ({ label, value, full, highlight }) => (
  <div className={full ? 'col-span-2' : ''}>
    <div className="text-[10px] uppercase text-white/50 font-bold">{label}</div>
    <div className="text-sm font-bold text-white mt-0.5"
      style={highlight ? { color: highlight } : {}}>{value}</div>
  </div>
);

export default Incidencias;
